import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { firestore, functions as firebaseFunctions } from '../firebase';
import { getActiveEventId, resolveAdminDefaultEventId } from '../utils/activeEvent';
import { getEventEditionYear } from '../utils/eventRouting';
import {
  distinctSortedDates,
  sortShiftsForDisplay,
  weekdayNameFromIsoDate,
  formatDayTabLabel,
  downloadVolunteerShiftsCsv,
  isShiftFull,
  isHighPriorityGap,
  deriveStartTimeFromTimeBlock,
  buildVolunteerDayTimeline,
  getVolunteerTimePillDisplay,
  formatTimeBlockDisplay,
  minuteSortKeyForShift,
} from '../utils/volunteerShifts';
import './VolunteerShiftAdmin.css';
import './admin/FmModal.css';
import { useModalEscapeKey } from '../utils/useModalEscapeKey';
import { getDisplayName, volunteerAssigneeDisplayName } from '../utils/flyerDisplay';
import { isRegisteredFlyerForVolunteerSignup } from '../utils/volunteerGateMatch';

const emptyForm = () => ({
  role: '',
  timeBlock: '',
  startTime: '',
  needed: 2,
  dayType: 'shoulder',
  date: '',
  description: '',
  notes: '',
});

function initialsFromName(displayName) {
  const t = (displayName || '').trim();
  if (!t) return '?';
  const parts = t.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    const a = parts[0][0] || '';
    const b = parts[1][0] || '';
    const out = `${a}${b}`.toUpperCase();
    return out || '?';
  }
  return (t.slice(0, 2) || '?').toUpperCase();
}

function shiftToForm(s) {
  return {
    role: s.role || '',
    timeBlock: s.timeBlock || '',
    needed: s.needed ?? 1,
    dayType: s.dayType === 'event' ? 'event' : 'shoulder',
    date: s.date || '',
    description: s.description || '',
    notes: s.notes || '',
    startTime: s.startTime || deriveStartTimeFromTimeBlock(s.timeBlock),
  };
}

export default function VolunteerShiftAdmin() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [activeEventId, setActiveEventId] = useState('');
  const [selectedEventId, setSelectedEventId] = useState('');
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [rosterShift, setRosterShift] = useState(null);
  /** When true, roster modal opens with emphasis on filling open spots (copy public link). */
  const [rosterAssignFocus, setRosterAssignFocus] = useState(false);
  const [rosterLinkCopied, setRosterLinkCopied] = useState(false);
  const rosterAssignBannerRef = useRef(null);
  const [cloneFrom, setCloneFrom] = useState('');
  const [cloneTo, setCloneTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [backfillBusy, setBackfillBusy] = useState(false);
  const [backfillSummary, setBackfillSummary] = useState(null);
  const [flyers, setFlyers] = useState([]);
  const [rosterAddFlyerId, setRosterAddFlyerId] = useState('');
  /** 'overview' = roster health + gaps + by-volunteer; 'list' = shifts by time block; 'grid' = timeline */
  const [dayView, setDayView] = useState('list');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [evSnap, activeId] = await Promise.all([
          getDocs(collection(firestore, 'events')),
          getActiveEventId(),
        ]);
        if (cancelled) return;
        const list = evSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        list.sort((a, b) => String(b.editionYear || '').localeCompare(String(a.editionYear || '')));
        setEvents(list);
        setActiveEventId(activeId || '');
        const param = (searchParams.get('event') || '').trim();
        const def = resolveAdminDefaultEventId({
          eventIdParam: param,
          activeEventId: activeId,
          events: list,
        });
        setSelectedEventId(def);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load events.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  const loadShifts = useCallback(async (eventId) => {
    if (!eventId) {
      setShifts([]);
      return;
    }
    setError('');
    try {
      const q = query(collection(firestore, 'volunteerShifts'), where('event', '==', eventId));
      const snap = await getDocs(q);
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setShifts(rows);
      const dates = distinctSortedDates(rows);
      setSelectedDate((d) => {
        if (d && dates.includes(d)) return d;
        return dates[0] || '';
      });
    } catch (e) {
      setError(e.message || 'Failed to load shifts.');
      setShifts([]);
    }
  }, []);

  useEffect(() => {
    loadShifts(selectedEventId);
  }, [selectedEventId, loadShifts]);

  useEffect(() => {
    if (!selectedEventId) {
      setFlyers([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(query(collection(firestore, 'flyers'), where('eventId', '==', selectedEventId)));
        if (!cancelled) {
          setFlyers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        }
      } catch {
        if (!cancelled) setFlyers([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedEventId]);

  const flyerByIdentityUid = useMemo(() => {
    const m = new Map();
    for (const f of flyers) {
      const u = (f.identityUid || '').toString().trim();
      if (u) m.set(u, f);
    }
    return m;
  }, [flyers]);

  const dates = useMemo(() => distinctSortedDates(shifts), [shifts]);
  const shiftsForDay = useMemo(
    () => sortShiftsForDisplay(shifts.filter((s) => (s.date || '') === selectedDate)),
    [shifts, selectedDate],
  );

  const dayTimeline = useMemo(() => buildVolunteerDayTimeline(shiftsForDay), [shiftsForDay]);

  const publicVolunteerTo = useMemo(() => {
    const q = selectedEventId ? `?event=${encodeURIComponent(selectedEventId)}` : '';
    return `/volunteer${q}`;
  }, [selectedEventId]);

  const publicVolunteerUrl = useMemo(() => {
    const path = publicVolunteerTo.startsWith('/') ? publicVolunteerTo : `/${publicVolunteerTo}`;
    if (typeof window === 'undefined') return path;
    return `${window.location.origin}${path}`;
  }, [publicVolunteerTo]);

  const rosterDayStats = useMemo(() => {
    let emptySlots = 0;
    let fullShifts = 0;
    const uidSet = new Set();
    for (const s of shiftsForDay) {
      const need = Number(s.needed) || 0;
      const claimed = Array.isArray(s.assignees) ? s.assignees.length : 0;
      emptySlots += Math.max(0, need - claimed);
      if (isShiftFull(s)) fullShifts += 1;
      for (const a of s.assignees || []) {
        const u = a?.uid != null ? String(a.uid).trim() : '';
        if (u) uidSet.add(u);
      }
    }
    return { emptySlots, volunteerCount: uidSet.size, fullShifts };
  }, [shiftsForDay]);

  const shiftsByTimeBlockAdmin = useMemo(() => {
    const sorted = sortShiftsForDisplay(shiftsForDay);
    if (!sorted.length) return [];
    const map = new Map();
    for (const s of sorted) {
      const tb = (s.timeBlock || '').toString().trim() || '—';
      if (!map.has(tb)) map.set(tb, []);
      map.get(tb).push(s);
    }
    return [...map.entries()]
      .sort((a, b) => {
        const da = minuteSortKeyForShift(a[1][0]);
        const db = minuteSortKeyForShift(b[1][0]);
        if (da !== db) return da - db;
        return (a[0] || '').localeCompare(b[0] || '');
      })
      .map(([timeBlock, blockShifts]) => {
        const shiftsInBlock = sortShiftsForDisplay(blockShifts);
        const emptySlots = shiftsInBlock.reduce((acc, s) => {
          const need = Number(s.needed) || 0;
          const claimed = Array.isArray(s.assignees) ? s.assignees.length : 0;
          return acc + Math.max(0, need - claimed);
        }, 0);
        return { timeBlock, shifts: shiftsInBlock, emptySlots };
      });
  }, [shiftsForDay]);

  const coverageGapRows = useMemo(() => {
    const rows = sortShiftsForDisplay(shiftsForDay).flatMap((s) => {
      const need = Number(s.needed) || 0;
      const claimed = Array.isArray(s.assignees) ? s.assignees.length : 0;
      const n = Math.max(0, need - claimed);
      return Array.from({ length: n }, () => ({ shift: s }));
    });
    rows.sort((a, b) => {
      const da = minuteSortKeyForShift(a.shift);
      const db = minuteSortKeyForShift(b.shift);
      if (da !== db) return da - db;
      return (a.shift.role || '').localeCompare(b.shift.role || '');
    });
    return rows;
  }, [shiftsForDay]);

  const volunteerAssignmentRows = useMemo(() => {
    const byUid = new Map();
    for (const s of shiftsForDay) {
      for (const a of s.assignees || []) {
        if (!a?.uid || typeof a.uid !== 'string') continue;
        const uid = a.uid.trim();
        if (!uid) continue;
        const resolved = volunteerAssigneeDisplayName(a, flyerByIdentityUid);
        if (!byUid.has(uid)) {
          byUid.set(uid, { uid, displayName: resolved || uid, shifts: [] });
        } else {
          const row = byUid.get(uid);
          const nm = volunteerAssigneeDisplayName(a, flyerByIdentityUid);
          if (nm && nm.length > (row.displayName || '').length && nm !== uid) row.displayName = nm;
        }
        byUid.get(uid).shifts.push(s);
      }
    }
    return [...byUid.values()]
      .map((row) => ({ ...row, shifts: sortShiftsForDisplay(row.shifts) }))
      .sort((a, b) =>
        (a.displayName || '').localeCompare(b.displayName || '', undefined, { sensitivity: 'base' }),
      );
  }, [shiftsForDay, flyerByIdentityUid]);

  const addableFlyersForRoster = useMemo(() => {
    if (!rosterShift || !flyers.length) return [];
    const ev = (rosterShift.event || '').toString().trim();
    if (!ev) return [];
    const onShift = new Set((rosterShift.assignees || []).map((x) => x?.uid).filter(Boolean));
    return flyers
      .filter((f) => (f.eventId || '') === ev)
      .filter((f) => isRegisteredFlyerForVolunteerSignup(f))
      .filter((f) => (f.identityUid || '').toString().trim())
      .filter((f) => !onShift.has((f.identityUid || '').toString().trim()))
      .slice()
      .sort((a, b) =>
        (getDisplayName(a) || a.email || '').localeCompare(getDisplayName(b) || b.email || '', undefined, {
          sensitivity: 'base',
        }),
      );
  }, [rosterShift, flyers]);

  const onEventChange = (id) => {
    setSelectedEventId(id);
    const next = new URLSearchParams(searchParams);
    if (id) next.set('event', id);
    else next.delete('event');
    setSearchParams(next, { replace: true });
  };

  const openAdd = () => {
    const d = selectedDate || dates[0] || '';
    setForm({
      ...emptyForm(),
      date: d,
      dayType: 'shoulder',
    });
    setEditId(null);
    setAddOpen(true);
  };

  const openEdit = (s) => {
    setForm(shiftToForm(s));
    setEditId(s.id);
    setAddOpen(true);
  };

  const closeModal = () => {
    setAddOpen(false);
    setEditId(null);
    setForm(emptyForm());
  };

  useModalEscapeKey(addOpen, closeModal, { enabled: !busy });
  const closeRosterModal = useCallback(() => {
    setRosterShift(null);
    setRosterAssignFocus(false);
    setRosterLinkCopied(false);
    setRosterAddFlyerId('');
  }, []);

  const openRosterModal = useCallback((shift, opts) => {
    setRosterShift(shift);
    setRosterAssignFocus(!!opts?.assignFocus);
    setRosterLinkCopied(false);
    setRosterAddFlyerId('');
  }, []);

  const copyVolunteerPageLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(publicVolunteerUrl);
      setRosterLinkCopied(true);
      window.setTimeout(() => setRosterLinkCopied(false), 2000);
    } catch {
      setError('Could not copy link — open the public volunteer page from the toolbar and copy the URL.');
    }
  }, [publicVolunteerUrl]);

  useLayoutEffect(() => {
    if (rosterShift && rosterAssignFocus && rosterAssignBannerRef.current) {
      rosterAssignBannerRef.current.focus({ preventScroll: true });
    }
  }, [rosterShift, rosterAssignFocus]);

  useModalEscapeKey(!!rosterShift, closeRosterModal);

  const saveShift = async () => {
    if (!selectedEventId) return;
    const date = (form.date || '').trim();
    if (!date || !form.role.trim() || !form.timeBlock.trim()) {
      setError('Date, role, and time block are required.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const day = weekdayNameFromIsoDate(date) || 'Day';
      const startTime = (form.startTime || '').trim() || deriveStartTimeFromTimeBlock(form.timeBlock);
      const payload = {
        event: selectedEventId,
        day,
        date,
        dayType: form.dayType === 'event' ? 'event' : 'shoulder',
        role: form.role.trim(),
        timeBlock: form.timeBlock.trim(),
        startTime,
        needed: Math.max(1, Number(form.needed) || 1),
        description: (form.description || '').trim(),
        notes: (form.notes || '').trim(),
        updatedAt: serverTimestamp(),
      };
      if (editId) {
        await updateDoc(doc(firestore, 'volunteerShifts', editId), payload);
      } else {
        await addDoc(collection(firestore, 'volunteerShifts'), {
          ...payload,
          assignees: [],
          createdAt: serverTimestamp(),
        });
      }
      closeModal();
      await loadShifts(selectedEventId);
    } catch (e) {
      setError(e.message || 'Save failed.');
    } finally {
      setBusy(false);
    }
  };

  const removeShift = async (id) => {
    if (!window.confirm('Delete this shift slot?')) return;
    setBusy(true);
    try {
      await deleteDoc(doc(firestore, 'volunteerShifts', id));
      await loadShifts(selectedEventId);
    } catch (e) {
      setError(e.message || 'Delete failed.');
    } finally {
      setBusy(false);
    }
  };

  const removeAssignee = async (shift, uid) => {
    const next = (Array.isArray(shift.assignees) ? shift.assignees : []).filter((a) => a && a.uid !== uid);
    setBusy(true);
    try {
      await updateDoc(doc(firestore, 'volunteerShifts', shift.id), {
        assignees: next,
        updatedAt: serverTimestamp(),
      });
      await loadShifts(selectedEventId);
      setRosterShift((prev) => (prev && prev.id === shift.id ? { ...shift, assignees: next } : prev));
    } catch (e) {
      setError(e.message || 'Could not update roster.');
    } finally {
      setBusy(false);
    }
  };

  const addFlyerToRoster = async () => {
    if (!rosterShift || !rosterAddFlyerId) return;
    const flyer = flyers.find((f) => f.id === rosterAddFlyerId);
    if (!flyer) {
      setError('Flyer not found.');
      return;
    }
    const uid = (flyer.identityUid || '').toString().trim();
    if (!uid) {
      setError('This flyer has no identity UID — run Backfill identity UID, then try again.');
      return;
    }
    if ((flyer.eventId || '').toString().trim() !== (rosterShift.event || '').toString().trim()) {
      setError('Flyer is not registered for this event.');
      return;
    }
    if (!isRegisteredFlyerForVolunteerSignup(flyer)) {
      setError('Only active freshling, mentor, or volunteer flyers can be added.');
      return;
    }
    const current = Array.isArray(rosterShift.assignees) ? rosterShift.assignees : [];
    if (current.some((a) => a && a.uid === uid)) {
      setError('That person is already on this roster.');
      return;
    }
    const need = Number(rosterShift.needed) || 0;
    if (need > 0 && current.length >= need) {
      setError('This shift is full.');
      return;
    }
    const displayName = (getDisplayName(flyer) || (flyer.email || '').toString().trim() || uid).slice(0, 120);
    const next = current.concat([
      {
        uid,
        displayName,
        claimedAt: Timestamp.now(),
        adminAdded: true,
      },
    ]);
    setBusy(true);
    setError('');
    try {
      await updateDoc(doc(firestore, 'volunteerShifts', rosterShift.id), {
        assignees: next,
        updatedAt: serverTimestamp(),
      });
      await loadShifts(selectedEventId);
      setRosterShift((prev) => (prev && prev.id === rosterShift.id ? { ...prev, assignees: next } : prev));
      setRosterAddFlyerId('');
    } catch (e) {
      setError(e.message || 'Could not add to roster.');
    } finally {
      setBusy(false);
    }
  };

  const runCloneDay = async () => {
    const from = (cloneFrom || '').trim();
    const to = (cloneTo || '').trim();
    if (!from || !to || from === to) {
      setError('Choose two different dates to clone.');
      return;
    }
    const src = shifts.filter((s) => (s.date || '') === from);
    if (!src.length) {
      setError('No shifts on the source day.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const dayName = weekdayNameFromIsoDate(to) || 'Day';
      for (const s of src) {
        await addDoc(collection(firestore, 'volunteerShifts'), {
          event: selectedEventId,
          day: dayName,
          date: to,
          dayType: s.dayType === 'event' ? 'event' : 'shoulder',
          role: s.role || '',
          timeBlock: s.timeBlock || '',
          startTime: s.startTime || deriveStartTimeFromTimeBlock(s.timeBlock),
          needed: Math.max(1, Number(s.needed) || 1),
          assignees: [],
          description: (s.description || '').trim(),
          notes: s.notes || '',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
      setCloneFrom('');
      setCloneTo('');
      await loadShifts(selectedEventId);
      setSelectedDate(to);
    } catch (e) {
      setError(e.message || 'Clone failed.');
    } finally {
      setBusy(false);
    }
  };

  const exportCsv = () => {
    const ev = events.find((e) => e.id === selectedEventId);
    const y = getEventEditionYear(ev) || 'event';
    downloadVolunteerShiftsCsv(shifts, `volunteer-shifts-${y}.csv`);
  };

  const runUidBackfill = async () => {
    if (!selectedEventId || backfillBusy) return;
    const ok = window.confirm('Backfill identityUid for flyers on this event now?');
    if (!ok) return;
    setBackfillBusy(true);
    setBackfillSummary(null);
    setError('');
    try {
      const fn = httpsCallable(firebaseFunctions, 'backfillFlyerIdentityUid');
      const res = await fn({ eventId: selectedEventId, dryRun: false });
      const out = res?.data || {};
      setBackfillSummary({
        scanned: Number(out.scanned) || 0,
        eligible: Number(out.eligible) || 0,
        alreadySet: Number(out.alreadySet) || 0,
        updated: Number(out.updated) || 0,
        unresolved: Number(out.unresolved) || 0,
        conflicts: Number(out.conflicts) || 0,
      });
    } catch (e) {
      setError(e.message || 'UID backfill failed.');
    } finally {
      setBackfillBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="volunteer-admin">
        <p>Loading…</p>
      </div>
    );
  }

  return (
    <div className="volunteer-admin">
      <header className="volunteer-admin__header">
        <h1 className="volunteer-admin__title">Volunteer shifts</h1>
        <p className="volunteer-admin__sub">Create slots, manage rosters, export CSV.</p>
      </header>

      {error && <p className="volunteer-admin__error">{error}</p>}

      <div className="volunteer-admin__toolbar">
        <label className="volunteer-admin__label">
          Event
          <select
            className="volunteer-admin__select"
            value={selectedEventId}
            onChange={(e) => onEventChange(e.target.value)}
          >
            {events.map((ev) => (
              <option key={ev.id} value={ev.id}>
                {getEventEditionYear(ev) || ev.title || ev.id}
                {ev.id === activeEventId ? ' (active default)' : ''}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="volunteer-admin__btn volunteer-admin__btn--primary btn-fm-primary" onClick={openAdd} disabled={!selectedEventId}>
          Add shift
        </button>
        <button type="button" className="volunteer-admin__btn volunteer-admin__btn--ghost" onClick={exportCsv} disabled={!shifts.length}>
          Export CSV
        </button>
        <button type="button" className="volunteer-admin__btn volunteer-admin__btn--ghost" onClick={runUidBackfill} disabled={!selectedEventId || backfillBusy}>
          {backfillBusy ? 'Backfilling…' : 'Backfill identity UID'}
        </button>
        <Link
          className="volunteer-admin__toolbar-link"
          to={publicVolunteerTo}
          target="_blank"
          rel="noopener noreferrer"
        >
          Public volunteer page
        </Link>
      </div>
      {backfillSummary && (
        <p className="volunteer-admin__backfill-summary">
          UID backfill complete: {backfillSummary.updated} updated, {backfillSummary.alreadySet} already set, {backfillSummary.unresolved} unresolved,{' '}
          {backfillSummary.conflicts} conflicts ({backfillSummary.eligible} eligible / {backfillSummary.scanned} scanned).
        </p>
      )}

      <section className="volunteer-admin__clone">
        <h2 className="volunteer-admin__section-title">Clone day</h2>
        <div className="volunteer-admin__clone-row">
          <label>
            From
            <select className="volunteer-admin__select" value={cloneFrom} onChange={(e) => setCloneFrom(e.target.value)}>
              <option value="">—</option>
              {dates.map((d) => (
                <option key={d} value={d}>
                  {formatDayTabLabel(d)}
                </option>
              ))}
            </select>
          </label>
          <label>
            To
            <input className="volunteer-admin__input" type="date" value={cloneTo} onChange={(e) => setCloneTo(e.target.value)} />
          </label>
          <button type="button" className="volunteer-admin__btn volunteer-admin__btn--primary btn-fm-primary" disabled={busy} onClick={runCloneDay}>
            Clone structure
          </button>
        </div>
      </section>

      <div className="volunteer-admin__day-tabs" role="tablist">
        {dates.map((d) => (
          <button
            key={d}
            type="button"
            role="tab"
            className={`volunteer-admin__day-tab${d === selectedDate ? ' volunteer-admin__day-tab--active' : ''}`}
            onClick={() => setSelectedDate(d)}
          >
            {formatDayTabLabel(d)}
          </button>
        ))}
      </div>

      {!selectedDate && <p className="volunteer-admin__empty">No shifts yet. Add a shift to define a day.</p>}

      {selectedDate && (
        <>
          <div className="volunteer-admin__view-toggle" role="group" aria-label="Day view layout">
            <span className="volunteer-admin__view-toggle-label">View</span>
            <button
              type="button"
              className={`volunteer-admin__view-toggle-btn${dayView === 'overview' ? ' volunteer-admin__view-toggle-btn--active' : ''}`}
              onClick={() => setDayView('overview')}
            >
              Roster
            </button>
            <button
              type="button"
              className={`volunteer-admin__view-toggle-btn${dayView === 'list' ? ' volunteer-admin__view-toggle-btn--active' : ''}`}
              onClick={() => setDayView('list')}
            >
              Shifts
            </button>
            <button
              type="button"
              className={`volunteer-admin__view-toggle-btn${dayView === 'grid' ? ' volunteer-admin__view-toggle-btn--active' : ''}`}
              onClick={() => setDayView('grid')}
            >
              Timeline
            </button>
          </div>

          {dayView === 'overview' && (
            <div className="volunteer-admin__roster-overview">
              <div className="volunteer-admin__summary-bar" role="status">
                <div className="volunteer-admin__summary-item">
                  <span className="volunteer-admin__summary-value">{rosterDayStats.emptySlots}</span>
                  <span className="volunteer-admin__summary-label">empty slots</span>
                </div>
                <div className="volunteer-admin__summary-item">
                  <span className="volunteer-admin__summary-value">{rosterDayStats.volunteerCount}</span>
                  <span className="volunteer-admin__summary-label">volunteers signed up</span>
                </div>
                <div className="volunteer-admin__summary-item">
                  <span className="volunteer-admin__summary-value">{rosterDayStats.fullShifts}</span>
                  <span className="volunteer-admin__summary-label">fully covered shifts</span>
                </div>
              </div>

              <div className="volunteer-admin__roster-overview-panels">
                <section className="volunteer-admin__panel volunteer-admin__panel--gaps">
                  <h3 className="volunteer-admin__panel-title">Coverage gaps</h3>
                  {coverageGapRows.length === 0 ? (
                    <p className="volunteer-admin__panel-empty">All slots filled for this day.</p>
                  ) : (
                    <ul className="volunteer-admin__gap-list">
                      {coverageGapRows.map(({ shift: s }, idx) => {
                        const pill = getVolunteerTimePillDisplay(s);
                        const pillClass = [
                          'volunteer-admin__time-pill',
                          pill.variant === 'allday' ? 'volunteer-admin__time-pill--allday' : '',
                          pill.variant === 'fallback' ? 'volunteer-admin__time-pill--fallback' : '',
                        ]
                          .filter(Boolean)
                          .join(' ');
                        const pillText = pill.variant === 'allday' ? 'All day' : pill.text;
                        return (
                          <li key={`${s.id}-${idx}`} className="volunteer-admin__gap-row">
                            <div className="volunteer-admin__gap-row-main">
                              <span className={pillClass}>{pillText}</span>
                              <span className="volunteer-admin__gap-role">{s.role}</span>
                              <span className="volunteer-admin__gap-meta">{s.dayType}</span>
                            </div>
                            <button
                              type="button"
                              className="volunteer-admin__btn volunteer-admin__btn--sm volunteer-admin__btn--assign"
                              onClick={() => openRosterModal(s, { assignFocus: true })}
                            >
                              + Assign
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </section>

                <section className="volunteer-admin__panel volunteer-admin__panel--volunteers">
                  <h3 className="volunteer-admin__panel-title">Volunteer assignments</h3>
                  {volunteerAssignmentRows.length === 0 ? (
                    <p className="volunteer-admin__panel-empty">No sign-ups yet for this day.</p>
                  ) : (
                    <ul className="volunteer-admin__volunteer-list">
                      {volunteerAssignmentRows.map((row) => (
                        <li key={row.uid} className="volunteer-admin__volunteer-row">
                          <div className="volunteer-admin__volunteer-identity">
                            <span className="volunteer-admin__volunteer-avatar" aria-hidden="true">
                              {initialsFromName(row.displayName)}
                            </span>
                            <span className="volunteer-admin__volunteer-name">{row.displayName}</span>
                            <span className="volunteer-admin__volunteer-count">{row.shifts.length} shift{row.shifts.length === 1 ? '' : 's'}</span>
                          </div>
                          <div className="volunteer-admin__volunteer-chips">
                            {row.shifts.map((s) => (
                              <button
                                key={s.id}
                                type="button"
                                className="volunteer-admin__shift-chip volunteer-admin__shift-chip--orange"
                                title={`${s.role} · ${formatTimeBlockDisplay(s.timeBlock)} — open roster`}
                                onClick={() => openRosterModal(s)}
                              >
                                <span className="volunteer-admin__shift-chip-role">{s.role}</span>
                                <span className="volunteer-admin__shift-chip-time">{formatTimeBlockDisplay(s.timeBlock)}</span>
                              </button>
                            ))}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              </div>
            </div>
          )}

          {dayView === 'list' && shiftsByTimeBlockAdmin.length === 0 && (
            <p className="volunteer-admin__empty">No shifts this day.</p>
          )}

          {dayView === 'list' && shiftsByTimeBlockAdmin.length > 0 && (
            <div className="volunteer-admin__shift-groups">
              {shiftsByTimeBlockAdmin.map(({ timeBlock, shifts: blockShifts, emptySlots }) => {
                const pill = blockShifts[0] ? getVolunteerTimePillDisplay(blockShifts[0]) : { variant: 'fallback', text: timeBlock };
                const pillClass = [
                  'volunteer-admin__time-pill',
                  pill.variant === 'allday' ? 'volunteer-admin__time-pill--allday' : '',
                  pill.variant === 'fallback' ? 'volunteer-admin__time-pill--fallback' : '',
                ]
                  .filter(Boolean)
                  .join(' ');
                const pillText = pill.variant === 'allday' ? 'All day' : pill.text;
                return (
                  <section key={timeBlock} className="volunteer-admin__time-block">
                    <div className="volunteer-admin__time-block-head">
                      <span className={pillClass}>{pillText}</span>
                      <span className="volunteer-admin__time-rule" aria-hidden />
                      <span
                        className={`volunteer-admin__time-count${emptySlots > 0 ? ' volunteer-admin__time-count--open' : ''}`}
                      >
                        {emptySlots} empty slot{emptySlots === 1 ? '' : 's'}
                      </span>
                    </div>
                    <div className="volunteer-admin__time-block-table-wrap">
                      <table className="volunteer-admin__table volunteer-admin__table--shifts">
                        <thead>
                          <tr>
                            <th>Role</th>
                            <th>Roster</th>
                            <th className="volunteer-admin__th-actions" />
                          </tr>
                        </thead>
                        <tbody>
                          {blockShifts.map((s) => {
                            const assignees = Array.isArray(s.assignees) ? s.assignees : [];
                            const need = Math.max(1, Number(s.needed) || 1);
                            const full = isShiftFull(s);
                            const gap = isHighPriorityGap(s);
                            const emptyChips = Math.max(0, need - assignees.length);
                            const rowGap = emptyChips > 0;
                            return (
                              <tr
                                key={s.id}
                                className={[
                                  gap ? 'volunteer-admin__row--gap' : '',
                                  rowGap ? 'volunteer-admin__shift-row--needs' : 'volunteer-admin__shift-row--full',
                                ]
                                  .filter(Boolean)
                                  .join(' ')}
                              >
                                <td className="volunteer-admin__td-role">
                                  <span className="volunteer-admin__role-name">{s.role}</span>
                                  <span className="volunteer-admin__role-daytype">{s.dayType}</span>
                                  {full && <span className="volunteer-admin__badge volunteer-admin__badge--inline">FULL</span>}
                                </td>
                                <td className="volunteer-admin__td-roster">
                                  <div className="volunteer-admin__roster-chips">
                                    {assignees.map((a) => (
                                      <span key={a.uid} className="volunteer-admin__person-chip volunteer-admin__person-chip--filled">
                                        {volunteerAssigneeDisplayName(a, flyerByIdentityUid)}
                                      </span>
                                    ))}
                                    {Array.from({ length: emptyChips }).map((_, i) => (
                                      <span key={`empty-${i}`} className="volunteer-admin__person-chip volunteer-admin__person-chip--empty">
                                        Open
                                      </span>
                                    ))}
                                  </div>
                                </td>
                                <td className="volunteer-admin__actions">
                                  <button type="button" className="volunteer-admin__btn volunteer-admin__btn--sm" onClick={() => openEdit(s)}>
                                    Edit
                                  </button>
                                  <button type="button" className="volunteer-admin__btn volunteer-admin__btn--sm" onClick={() => openRosterModal(s)}>
                                    Roster
                                  </button>
                                  <button
                                    type="button"
                                    className="volunteer-admin__btn volunteer-admin__btn--danger volunteer-admin__btn--sm"
                                    onClick={() => removeShift(s.id)}
                                  >
                                    Delete
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </section>
                );
              })}
            </div>
          )}

          {dayView === 'grid' && (
            <div className="volunteer-admin__grid-wrap volunteer-admin__timeline-wrap">
              <p className="volunteer-admin__grid-hint">
                Rows are roles. Each block is a shift on a linear time axis; overlapping times stack vertically. Use clear start/end times; 24h
                (e.g. 17:00) avoids ambiguity.
              </p>
              {!dayTimeline.window && !shiftsForDay.length && <p className="volunteer-admin__empty">No shifts this day.</p>}
              {!dayTimeline.window && shiftsForDay.length > 0 && (
                <p className="volunteer-admin__empty">
                  No shift could be placed on the timeline. Check each slot has a parseable time block or start time (HH:mm).
                </p>
              )}
              {dayTimeline.window && (() => {
                const w = dayTimeline.window;
                const rangeMin = Math.max(1, w.endMin - w.startMin);
                const laneHeightPx = 56;
                return (
                  <div className="volunteer-admin__timeline-scroll">
                    <div
                      className="volunteer-admin__timeline-grid"
                      style={{
                        '--volunteer-timeline-range': String(rangeMin),
                        '--volunteer-timeline-start': String(w.startMin),
                      }}
                    >
                      <div className="volunteer-admin__timeline-sticky-label volunteer-admin__timeline-corner">Time</div>
                      <div className="volunteer-admin__timeline-axis" aria-hidden="true">
                        {dayTimeline.hourTicks.map((tick) => (
                          <span
                            key={tick.minutes}
                            className="volunteer-admin__timeline-tick"
                            style={{ left: `${((tick.minutes - w.startMin) / rangeMin) * 100}%` }}
                          >
                            {tick.label}
                          </span>
                        ))}
                      </div>
                      {dayTimeline.roleRows.map((row) => (
                        <React.Fragment key={row.role}>
                          <div className="volunteer-admin__timeline-sticky-label volunteer-admin__timeline-role-label">{row.role}</div>
                          <div
                            className="volunteer-admin__timeline-track"
                            style={{
                              minHeight: row.laneCount * laneHeightPx + 8,
                              '--volunteer-hour-stripe-pct': `${(60 / rangeMin) * 100}%`,
                            }}
                          >
                            {row.items.map(({ shift: s, interval, lane }) => {
                              const claimed = Array.isArray(s.assignees) ? s.assignees.length : 0;
                              const full = isShiftFull(s);
                              const gap = isHighPriorityGap(s);
                              const leftPct = ((interval.start - w.startMin) / rangeMin) * 100;
                              const widthPct = ((interval.end - interval.start) / rangeMin) * 100;
                              return (
                                <div
                                  key={s.id}
                                  className={`volunteer-admin__timeline-bar${gap ? ' volunteer-admin__timeline-bar--gap' : ''}`}
                                  style={{
                                    left: `${Math.max(0, Math.min(100, leftPct))}%`,
                                    width: `${Math.max(0.5, Math.min(100, widthPct))}%`,
                                    top: lane * laneHeightPx + 4,
                                  }}
                                  title={s.timeBlock || `${s.startTime || ''}`}
                                >
                                  <div className="volunteer-admin__timeline-bar-inner">
                                    <div className="volunteer-admin__timeline-bar-meta">
                                      <span className="volunteer-admin__grid-daytype">{s.dayType}</span>
                                      <span className="volunteer-admin__grid-count">
                                        {claimed}/{s.needed}
                                        {full && <span className="volunteer-admin__badge">FULL</span>}
                                      </span>
                                    </div>
                                    <div className="volunteer-admin__timeline-bar-actions">
                                      <button type="button" className="volunteer-admin__btn volunteer-admin__btn--sm" onClick={() => openRosterModal(s)}>
                                        Roster
                                      </button>
                                      <button type="button" className="volunteer-admin__btn volunteer-admin__btn--sm" onClick={() => openEdit(s)}>
                                        Edit
                                      </button>
                                      <button
                                        type="button"
                                        className="volunteer-admin__btn volunteer-admin__btn--danger volunteer-admin__btn--sm"
                                        onClick={() => removeShift(s.id)}
                                      >
                                        Delete
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                );
              })()}
              {dayTimeline.unplaced.length > 0 && (
                <div className="volunteer-admin__timeline-unplaced">
                  <strong className="volunteer-admin__timeline-unplaced-title">Not on timeline</strong>
                  <p className="volunteer-admin__timeline-unplaced-hint">Fix time block / start time (HH:mm), or use 24h end times.</p>
                  <ul className="volunteer-admin__timeline-unplaced-list">
                    {dayTimeline.unplaced.map((s) => (
                      <li key={s.id} className="volunteer-admin__timeline-unplaced-row">
                        <span>
                          {s.role} — {s.timeBlock || '—'} (start {s.startTime || '—'})
                        </span>
                        <button type="button" className="volunteer-admin__btn volunteer-admin__btn--sm" onClick={() => openEdit(s)}>
                          Edit
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {addOpen && (
        <div className="fm-modal-overlay" role="presentation" onClick={() => !busy && closeModal()}>
          <div
            className="fm-modal fm-modal--md volunteer-admin__modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="volunteer-shift-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="fm-modal-header">
              <h2 className="fm-modal-title" id="volunteer-shift-modal-title">
                {editId ? 'Edit shift' : 'Add shift'}
              </h2>
              <button type="button" className="fm-modal-close" onClick={() => !busy && closeModal()} aria-label="Close">
                ✕
              </button>
            </div>
            <div className="fm-modal-body">
            <div className="volunteer-admin__field">
              <label>Date</label>
              <input type="date" className="volunteer-admin__input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
            <div className="volunteer-admin__field">
              <label>Day type</label>
              <select className="volunteer-admin__select" value={form.dayType} onChange={(e) => setForm({ ...form, dayType: e.target.value })}>
                <option value="shoulder">shoulder</option>
                <option value="event">event</option>
              </select>
            </div>
            <div className="volunteer-admin__field">
              <label>Role</label>
              <input className="volunteer-admin__input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder="e.g. Setup Crew" />
            </div>
            <div className="volunteer-admin__field">
              <label>Volunteer description (public)</label>
              <textarea
                className="volunteer-admin__input"
                rows={2}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Shown on the volunteer sign-up page under the task name"
              />
            </div>
            <div className="volunteer-admin__field">
              <label>Time block</label>
              <input className="volunteer-admin__input" value={form.timeBlock} onChange={(e) => setForm({ ...form, timeBlock: e.target.value })} placeholder="9:00–11:00" />
            </div>
            <div className="volunteer-admin__field">
              <label>Start time (sort key)</label>
              <input className="volunteer-admin__input" value={form.startTime || ''} onChange={(e) => setForm({ ...form, startTime: e.target.value })} placeholder="HH:mm" />
            </div>
            <div className="volunteer-admin__field">
              <label>Needed</label>
              <input type="number" min={1} className="volunteer-admin__input" value={form.needed} onChange={(e) => setForm({ ...form, needed: e.target.value })} />
            </div>
            <div className="volunteer-admin__field">
              <label>Notes (admin)</label>
              <input className="volunteer-admin__input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            </div>
            <div className="fm-modal-footer">
              <button type="button" className="fm-modal-btn fm-modal-btn-cancel" disabled={busy} onClick={closeModal}>
                Cancel
              </button>
              <button type="button" className="fm-modal-btn fm-modal-btn-primary" disabled={busy} onClick={saveShift}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {rosterShift && (
        <div className="fm-modal-overlay" role="presentation" onClick={closeRosterModal}>
          <div
            className="fm-modal fm-modal--md volunteer-admin__modal volunteer-admin__modal--wide"
            role="dialog"
            aria-modal="true"
            aria-labelledby="volunteer-roster-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="fm-modal-header">
              <h2 className="fm-modal-title" id="volunteer-roster-modal-title">
                Roster — {rosterShift.role} ({rosterShift.timeBlock})
              </h2>
              <button type="button" className="fm-modal-close" onClick={closeRosterModal} aria-label="Close">
                ✕
              </button>
            </div>
            <div className="fm-modal-body">
              {rosterAssignFocus && (() => {
                const claimed = Array.isArray(rosterShift.assignees) ? rosterShift.assignees.length : 0;
                const need = Number(rosterShift.needed) || 0;
                const openSlots = Math.max(0, need - claimed);
                return (
                  <div
                    ref={rosterAssignBannerRef}
                    tabIndex={-1}
                    className="volunteer-admin__roster-assign-banner"
                  >
                    <p className="volunteer-admin__roster-assign-lead">
                      <strong>{openSlots}</strong> open {openSlots === 1 ? 'spot' : 'spots'}. Volunteers join from the public sign-up page.
                    </p>
                    <div className="volunteer-admin__roster-assign-actions">
                      <button
                        type="button"
                        className="volunteer-admin__btn volunteer-admin__btn--primary volunteer-admin__btn--sm"
                        onClick={copyVolunteerPageLink}
                      >
                        {rosterLinkCopied ? 'Copied!' : 'Copy volunteer page link'}
                      </button>
                      <Link className="volunteer-admin__roster-assign-link" to={publicVolunteerTo} target="_blank" rel="noopener noreferrer">
                        Open public page
                      </Link>
                    </div>
                  </div>
                );
              })()}
              {!isShiftFull(rosterShift) && (
                <div className="volunteer-admin__roster-add">
                  <label className="volunteer-admin__roster-add-label" htmlFor="volunteer-roster-add-flyer">
                    Add flyer manually
                  </label>
                  <div className="volunteer-admin__roster-add-row">
                    <select
                      id="volunteer-roster-add-flyer"
                      className="volunteer-admin__select volunteer-admin__select--grow"
                      value={rosterAddFlyerId}
                      onChange={(e) => setRosterAddFlyerId(e.target.value)}
                      disabled={busy}
                    >
                      <option value="">Choose flyer…</option>
                      {addableFlyersForRoster.map((f) => (
                        <option key={f.id} value={f.id}>
                          {getDisplayName(f) || f.email || f.id} ({f.type})
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="volunteer-admin__btn volunteer-admin__btn--primary volunteer-admin__btn--sm"
                      disabled={busy || !rosterAddFlyerId}
                      onClick={() => addFlyerToRoster()}
                    >
                      Add
                    </button>
                  </div>
                  {addableFlyersForRoster.length === 0 && (
                    <p className="volunteer-admin__roster-add-hint">
                      No flyers left to add, or event flyers are missing identity UID — use Backfill identity UID on this event.
                    </p>
                  )}
                </div>
              )}
              <ul className="volunteer-admin__roster">
                {(rosterShift.assignees || []).length === 0 && <li>No sign-ups yet.</li>}
                {(rosterShift.assignees || []).map((a) => (
                  <li key={a.uid} className="volunteer-admin__roster-row">
                    <span>{volunteerAssigneeDisplayName(a, flyerByIdentityUid)}</span>
                    <button
                      type="button"
                      className="volunteer-admin__btn volunteer-admin__btn--danger volunteer-admin__btn--sm"
                      disabled={busy}
                      onClick={() => removeAssignee(rosterShift, a.uid)}
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            <div className="fm-modal-footer">
              <button type="button" className="fm-modal-btn fm-modal-btn-cancel" onClick={closeRosterModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
